import React, { useMemo } from 'react';
import { TooltipItemGOP } from '../../ui/TooltipGOP';
import Loading from 'src/components/Loading';;

interface TableRowProps {
  FilteGroup: any[];
  VPOStatus?: any[];
  GopDirections: any[];
  StatusData?: any[]; 
}

const calculatePercentageFromBackend = (answers: any[]): number | 'N/A' => {
  if (!answers || answers.length === 0) {
    return 'N/A';
  }

  const validAnswers = answers.filter((answer) => answer.answer !== 'N/A');

  if (validAnswers.length === 0) {
    return 'N/A';
  }

  const { sum, count } = validAnswers.reduce(
    (acc, question) => {
      if (question.answer === 'Да') {
        acc.sum += 100;
        acc.count += 1;
      } else if (question.answer === 'Нет') {
        acc.sum += 0;
        acc.count += 1;
      }

      return acc;
    },
    { sum: 0, count: 0 },
  );

  return count > 0 ? Math.round(sum / count) : 0;
};

export default function TableRowGop({ FilteGroup, VPOStatus, GopDirections, StatusData }: TableRowProps) {

  const dataReady = useMemo(() => {
    const ready = (VPOStatus?.length || 0) > 0 && (FilteGroup?.length || 0) > 0 && (GopDirections?.length || 0) > 0;

    return ready;
  }, [VPOStatus?.length, FilteGroup?.length, GopDirections?.length]);

  //! Расчет данных с единой логикой
  const preCalculatedData = useMemo(() => {
    if (!dataReady) {
      return {};
    }

    const results: any = {};

    FilteGroup!.forEach((factory) => {
      const factoryId = factory.id || factory.ID;
      results[factoryId] = {
        directions: {},
        totalYes: 0,
        totalValid: 0,
        averagePercentage: 0,
        status: 'Не начато',
      };

      GopDirections!.forEach((direction) => {
        const answers = VPOStatus!.filter((item) => 
          String(item.factory?.id) === String(factoryId) && 
          String(item.direction?.id) === String(direction.ID)
        );

        const percentage = calculatePercentageFromBackend(answers);
        const percentageValue = percentage === 'N/A' ? 0 : percentage;
        
        const validAnswers = answers.filter(answer => answer.answer !== 'N/A');
        const yesCount = validAnswers.filter(answer => answer.answer === 'Да').length;
        const totalValid = validAnswers.length;

        const statusEntry = StatusData?.find((item) => 
          String(item.factory?.id) === String(factoryId) && 
          String(item.direction?.id) === String(direction.ID)
        );

        const status = statusEntry?.statusQuestionnaire || 'Не начато';

        results[factoryId].directions[direction.ID] = {
          percentage: percentageValue,
          status, 
          yesCount,
          totalValid,
          totalAnswers: answers.length,
        };

        results[factoryId].totalYes += yesCount;
        results[factoryId].totalValid += totalValid;
      });

      const allFactoryAnswers = VPOStatus!.filter((item) => 
        String(item.factory?.id) === String(factoryId)
      );
      const factoryPercentage = calculatePercentageFromBackend(allFactoryAnswers);
      results[factoryId].averagePercentage = factoryPercentage === 'N/A' ? 0 : factoryPercentage;

      const directionStatuses = Object.values(results[factoryId].directions).map((dir: any) => dir.status);

      if (directionStatuses.every((status) => status === 'Завершено')) {
        results[factoryId].status = 'Завершено';
      } else if (directionStatuses.every((status) => status === 'Не начато')) {
        results[factoryId].status = 'Не начато';
      } else if (directionStatuses.some((status) => status === 'Завершено') || directionStatuses.some((status) => status === 'В процессе')) {
        results[factoryId].status = 'В процессе';
      }
    });

    return results;
  }, [dataReady, StatusData, VPOStatus]);

  const getStatusMarkerColor = (status: string) => {
    switch (status) {
      case 'Завершено':
        return 'bg-[#22AD5C]';
      case 'В процессе':
        return 'bg-[#F2A900]';
      default:
        return 'bg-[#8899A8]';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Завершено':
        return 'bg-[#22AD5C]';
      case 'В процессе':
        return 'bg-[#F2A900]';
      default:
        return 'bg-[#8899A8]';
    }
  };

  const Styles = {
    column: 'flex items-center justify-center px-3 py-2 rounded text-white text-sm font-medium w-[120px] h-8 overflow-hidden',
    thTable: 'py-3 px-3 text-left text-sm font-medium text-black',
  };

  const factoriesToShow = FilteGroup?.length > 0 ? FilteGroup : [];
  const directionsToShow = GopDirections?.length > 0 ? GopDirections : [];
  const hasAnyData = factoriesToShow.length > 0 && directionsToShow.length > 0;

  return (
    <div className='h-[calc(90vh-195px)] bg-white rounded-lg shadow'>
      <div className='h-full overflow-y-scroll scrollbar scrollbar-thumb-rounded-lg scrollbar-h-2 scrollbar-w-2 scrollbar-thumb-[#D1D5DB]'>
        <div className='sticky top-0 z-10 bg-gray-50 border-b border-gray-200'>
          <div className='flex'>
            <div className='flex-shrink-0'>
              <div className='flex'>
                <div className={`${Styles.thTable} w-[120px]`}>Статус</div>
                <div className={`${Styles.thTable} w-[220px]`}>Завод</div>
                <div className={`${Styles.thTable} w-[120px]`}>Группа</div>
                <div className={`${Styles.thTable} w-[200px] `}>Среднее по заводу</div>
              </div>
            </div>

            <div className='flex'>
              <div className={`${Styles.thTable} flex-1 pl-8`}>Статус по направлению</div>
            </div>
          </div>
        </div>

        {hasAnyData && (
          <div className='flex'>
            <div className='flex-shrink-0'>
              {factoriesToShow.map((factory) => {
                const factoryId = factory.id || factory.ID;
                const factoryData = dataReady
                  ? preCalculatedData[factoryId] || {
                      status: 'Не начато',
                      averagePercentage: 0,
                      directions: {},
                    }
                  : {
                      status: 'Не начато',
                      averagePercentage: 0,
                      directions: {},
                    };

                return (
                  <div key={factoryId} className='flex border-b border-gray-200 ' style={{ minHeight: '73px' }}>
                    <div className='py-4 px-4 w-[120px] flex items-center'>
                      <div className='flex items-center gap-2 text-sm font-normal text-primary whitespace-nowrap'>
                        <div className={`w-3 h-3 rounded ${getStatusMarkerColor(factoryData.status)}`}></div>
                        <span className='text-sm text-[#637381]'>{factoryData.status}</span>
                      </div>
                    </div>
                    <div className='py-4 px-4 w-[220px] flex items-center'>
                      <span className='text-sm text-[#637381]'>{factory.title}</span>
                    </div>
                    <div className='py-4 px-4 w-[120px] flex items-center'>
                      <span className='text-sm text-[#637381]'>{factory.group?.title || 'Не указано'}</span>
                    </div>
                    <div className='py-4 px-4 w-[200px] flex items-center'>
                      <div className='w-full max-w-xs'>
                        <div className='text-xs font-medium text-[#637381] mb-1'>Текущая оценка</div>
                        <div className='w-full bg-gray-200 h-5 rounded-full relative'>
                          <div
                            className={`h-full rounded-full flex items-center justify-center text-xs text-white font-normal ${getStatusColor(factoryData.status)} transition-all duration-300`}
                            style={{ width: `${factoryData.averagePercentage}%` }}
                          ></div>
                          <div className='absolute inset-0 flex items-center justify-center text-sm text-gray-600'>{factoryData.averagePercentage}%</div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className='flex-1 overflow-x-auto scrollbar scrollbar-thumb-rounded-lg scrollbar-h-2 scrollbar-w-1 scrollbar-thumb-[#D1D5DB]'>
              <div className='pl-6'>
                {factoriesToShow.map((factory, factoryIndex) => {
                  const factoryId = factory.id || factory.ID;
                  const factoryData = dataReady
                    ? preCalculatedData[factoryId] || {
                        status: 'Не начато',
                        averagePercentage: 0,
                        directions: {},
                      }
                    : {
                        status: 'Не начато',
                        averagePercentage: 0,
                        directions: {},
                      };

                  return (
                    <div key={factoryId} className='border-b border-gray-200 py-4 px-2 flex items-center' style={{ minHeight: '73px' }}>
                      <div className='flex gap-2 items-center'>
                        {directionsToShow.map((direction) => {
                          const directionData = dataReady
                            ? factoryData.directions[direction.ID] || {
                                percentage: 0,
                                status: 'Не начато',
                              }
                            : {
                                percentage: 0,
                                status: 'Не начато',
                              };

                          const displayCode = direction.Code;
                          const tooltipText = `${direction.Title}`;
                          // const tooltipText = `${direction.Title} - ${directionData.percentage}%`;
                          return (
                            <TooltipItemGOP position={'top'} tooltipsText={tooltipText} key={direction.ID}>
                              <span className={`${Styles.column} ${getStatusColor(directionData.status)} flex-shrink-0`}>
                                <span className="truncate block">{displayCode}</span>
                              </span>
                            </TooltipItemGOP>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}